import React from 'react';
import { ErrorMessage } from '@atlaskit/form';
import { renderPromotionOption } from '@css-xp/base';
import { Promotion } from '@css-xp/graphtypes';
import ManagePromotionField from './manage-apps-promocode-field';
import { usePartnerCatalogueContext } from '../context/partner-catalogue-context';
import { AppPromoCodeDetails } from '../types';

import './manage-apps-promocode-field.scss';

export type PromocodeDetails = {
  promocode: string;
} & Promotion;

type AppPromotionIdsProps = {
  productId: string;
  promotionId: string | undefined;
};

type AppPromoCodeDetailsProps = {
  productId: string;
  promotionDetails: AppPromoCodeDetails | undefined;
};

interface Props {
  productId: string;
  addPromocodeDetails?: (productId: string, promotion: PromocodeDetails) => void;
  removePromocodeDetails?: (productId: string) => void;
  isPromotionDetailHidden?: boolean;
  setAppsPromotionIds?: (_promotionIdsDetails: AppPromotionIdsProps) => void;
  setAppsPromotionDetails?: (_promotionCodeDetails: AppPromoCodeDetailsProps) => void;
  setPromotionDetails?: (_promotionDetails: any) => void;
  hideRemovePromotionCTA?: boolean;
  showApplyFieldOnRemove?: boolean;
  setShowApplyFieldOnRemove?: (_showApplyFieldOnRemove: boolean) => void;
  hideSuccessIconOnValidPromotion?: boolean;
}

export const ManageAppsPromocode = React.memo(
  ({
    productId,
    addPromocodeDetails,
    removePromocodeDetails,
    isPromotionDetailHidden = false,
    setAppsPromotionDetails,
    setPromotionDetails,
    hideRemovePromotionCTA,
    showApplyFieldOnRemove,
    setShowApplyFieldOnRemove,
    hideSuccessIconOnValidPromotion,
  }: Props) => {
    const {
      setAppsPromotionIds,
      setAppsPromotionIdsDetails,
      removeAppsPromotionId,
      removeAppsPromotionIdsDetails,
    } = usePartnerCatalogueContext();

    const [promoCodeErrors, setPromoCodeErrors] = React.useState<any>(null);
    const [validPromotionData, setValidPromotionData] = React.useState<any>({});

    const managePromocodeDetails = (promocodeDetails: any) => {
      setPromoCodeErrors(null);
      const isPromoCodeValid = promocodeDetails && Object.keys(promocodeDetails?.validPromotionData)?.length > 0;
      if (promocodeDetails?.promoCodeNotApplicableErrors) {
        setPromoCodeErrors(promocodeDetails?.promoCodeNotApplicableErrors);
        return;
      }
      if (isPromoCodeValid) {
        setShowApplyFieldOnRemove?.(false);
        setValidPromotionData(promocodeDetails?.validPromotionData);
        if (setAppsPromotionDetails) {
          /** This is required for new design UOF */
          setAppsPromotionDetails?.({ productId: productId, promotionDetails: promocodeDetails?.validPromotionData });
          setPromotionDetails?.({ productId: productId, promotionDetails: promocodeDetails?.validPromotionData });
        } else {
          /** These are required for old design UOF */
          setAppsPromotionIds(productId, promocodeDetails?.validPromotionData?.promocode);
          setAppsPromotionIdsDetails(productId, promocodeDetails?.validPromotionData);
        }

        addPromocodeDetails?.(productId, promocodeDetails?.validPromotionData as PromocodeDetails);
      }
    };

    const removePromocode = (productId: string) => {
      setPromoCodeErrors(null);
      setValidPromotionData({});
      removeAppsPromotionId(productId);
      removeAppsPromotionIdsDetails(productId); // NOTE: This is required only for unified order flow
      removePromocodeDetails?.(productId);
    };

    const isPromoCodeValid = validPromotionData && Object.keys(validPromotionData)?.length > 0;
    return (
      <div className="promotion-view-section">
        <ManagePromotionField
          productId={productId}
          removePromocode={removePromocode}
          managePromocodeDetails={managePromocodeDetails}
          isPromoCodeValid={isPromoCodeValid}
          hideRemovePromotionCTA={hideRemovePromotionCTA}
          showApplyFieldOnRemove={showApplyFieldOnRemove}
          hideSuccessIconOnValidPromotion={hideSuccessIconOnValidPromotion}
        />
        {isPromoCodeValid && !isPromotionDetailHidden && (
          <div className="promo-code-promotion-details">
            <div>{renderPromotionOption(validPromotionData, { showBenefitNextLine: true })}</div>
          </div>
        )}
        {promoCodeErrors && (
          <div className="promocode-applicable-error">
            <ErrorMessage>{promoCodeErrors}</ErrorMessage>
          </div>
        )}
      </div>
    );
  }
);
